<template>
  <div>
    <teleport to="body">
      <!-- Floating Assistant Orb -->
      <div class="va-assistant-orb" :class="{ 'android-device': isAndroid }" @click="handleAvatarClick">
        <v-tooltip location="top">
          <template #activator="{ props }">
            <div v-bind="props" class="va-orb-container" :class="{ active: isDialogOpen }">
              <div class="va-orb-glow"></div>
              <div class="va-orb-inner">
                <div ref="lottieAgentRef" class="va-orb-avatar"></div>
              </div>
              <div class="va-orb-pulse"></div>
              <div class="va-orb-pulse va-orb-pulse-2"></div>
            </div>
          </template>
          <span>{{ isDialogOpen ? $t('chat.agent.avatar.tooltip-listening') : $t('chat.agent.avatar.tooltip-ask') }}</span>
        </v-tooltip>
      </div>

      <!-- Modern Dialog Interface -->
      <transition name="va-dialog-transition">
        <div v-if="isDialogOpen" class="va-assistant-interface">
          <!-- Backdrop with blur -->
          <div class="va-interface-backdrop" @click="handleCancel"></div>

          <!-- Main Assistant Card -->
          <div class="va-assistant-card">
            <div style="position: absolute; top: 12px; right: 16px; z-index: 2;">
              <button class="va-close-btn" @click="handleCancel" style="margin-top: 0;">
                <i class="mdi mdi-close"></i>
              </button>
            </div>

            <!-- Scrollable Content -->
            <div class="va-card-body">
              <!-- Central Animation -->
              <div class="va-animation-section">
                <div class="va-animation-wrapper">
                  <div class="va-animation-backdrop"></div>
                  <div ref="lottieContainer" class="va-lottie-container" :class="{ speaking: hasStartedSpeaking }"></div>
                  <div class="va-sound-waves" v-if="hasStartedSpeaking && !isPaused">
                    <span class="va-wave"></span>
                    <span class="va-wave"></span>
                    <span class="va-wave"></span>
                  </div>
                </div>
              </div>

              <!-- Status Message -->
              <transition name="va-fade">
                <div v-if="showDelayMessage" class="va-status-message">
                  <span class="va-status-dot"></span>
                  {{ $t('chat.agent.delay.message') }}
                </div>
              </transition>

              <!-- Primary Controls -->
              <div class="va-controls-section">
                <button v-if="!hasStartedSpeaking" class="va-control-btn va-btn-primary" :disabled="isTextSending || isVoiceSending" @click="startSpeaking">
                  <i class="mdi mdi-microphone" style="font-size:30px"></i>
                  <span style="font-size:larger">{{ $t('chat.agent.buttons.speak') }}</span>
                </button>

                <button v-else class="va-control-btn va-btn-secondary" :disabled="isPausing || isTextSending || isVoiceSending" @click="togglePause">
                  <span v-if="isPausing" class="va-spinner"></span>
                  <i v-else style="font-size:30px" class="mdi" :class="isPaused ? 'mdi-play' : 'mdi-pause'"></i>
                  <span style="font-size:larger">{{ isPaused ? $t('chat.agent.buttons.resume') : $t('chat.agent.buttons.pause') }}</span>
                </button>

                <button class="va-control-btn va-btn-tertiary" :disabled="isTextSending || isVoiceSending" @click="openChatWindow">
                  <i class="mdi mdi-message-text" style="font-size:30px"></i>
                  <span style="font-size:larger">{{ $t('chat.agent.buttons.chat') }}</span>
                </button>
              </div>

              <!-- Send Actions -->
              <div class="va-send-actions">
                <button class="va-send-btn va-text-reply" :class="{ loading: isTextSending }" :disabled="isTextSending || isVoiceSending" @click="handleTextReply">
                  <div class="va-send-btn-icon">
                    <span v-if="isTextSending" class="va-spinner"></span>
                    <i v-else class="mdi mdi-keyboard"></i>
                  </div>
                  <div class="va-send-btn-content">
                    <strong>{{ $t('chat.agent.buttons.send') }}</strong>
                    <span>{{ $t('chat.agent.buttons.text-reply') }}</span>
                  </div>
                  <i class="mdi mdi-chevron-right va-send-btn-arrow"></i>
                </button>

                <button class="va-send-btn va-voice-reply" :class="{ loading: isVoiceSending }" :disabled="isTextSending || isVoiceSending" @click="handleVoiceReply">
                  <div class="va-send-btn-icon">
                    <span v-if="isVoiceSending" class="va-spinner"></span>
                    <i v-else class="mdi mdi-microphone-message"></i>
                  </div>
                  <div class="va-send-btn-content">
                    <strong>{{ $t('chat.agent.buttons.send') }}</strong>
                    <span>{{ $t('chat.agent.buttons.voice-reply') }}</span>
                  </div>
                  <i class="mdi mdi-chevron-right va-send-btn-arrow"></i>
                </button>
              </div>

              <!-- Controls Guide -->
              <div class="va-guide-section">
                <v-card class="va-guide-card" flat>
                  <div class="va-guide-item">
                    <v-chip class="va-guide-chip" color="white" label size="small" style="background:linear-gradient(135deg, #10b981 0%, #059669 100%);font-weight:bold">
                      <v-icon start size="small">mdi-microphone</v-icon>
                      {{ $t('chat.agent.controls-guide.speak.label') }}
                    </v-chip>
                    <span class="va-guide-text">{{ $t('chat.agent.controls-guide.speak.description') }}</span>
                  </div>
                  <div class="va-guide-item">
                    <v-chip
                      class="va-guide-chip"
                      color="white"
                      label
                      size="small"
                      style="background:linear-gradient(135deg, #818cf8 0%, #6366f1 50%, #ec4899 100%);font-weight:bold"
                    >
                      <v-icon start size="small">mdi-pause-circle</v-icon>
                      {{ $t('chat.agent.controls-guide.pause.label') }}
                    </v-chip>
                    <span class="va-guide-text">{{ $t('chat.agent.controls-guide.pause.description') }}</span>
                  </div>
                  <div class="va-guide-item">
                    <v-chip class="va-guide-chip" label size="small" color="white" style="background:linear-gradient(135deg, #38bdf8 0%, #2563eb 100%);font-weight:bold">
                      <v-icon start size="small">mdi-chat</v-icon>
                      {{ $t('chat.agent.controls-guide.chat.label') }}
                    </v-chip>
                    <span class="va-guide-text">{{ $t('chat.agent.controls-guide.chat.description') }}</span>
                  </div>
                  <div class="va-guide-item">
                    <v-chip class="va-guide-chip" color="white" label size="small" style="background:linear-gradient(135deg, #dc2626 0%, #991b1b 100%);font-weight:bold">
                      <v-icon start size="small">mdi-send</v-icon>
                      {{ $t('chat.agent.controls-guide.send-text.label') }}
                    </v-chip>
                    <span class="va-guide-text">{{ $t('chat.agent.controls-guide.send-text.description') }}</span>
                  </div>
                  <div class="va-guide-item">
                    <v-chip class="va-guide-chip" color="white" label size="small" style="background:linear-gradient(90deg, #E63946, #F28C38);font-weight:bold">
                      <v-icon start size="small">mdi-volume-high</v-icon>
                      {{ $t('chat.agent.controls-guide.send-voice.label') }}
                    </v-chip>
                    <span class="va-guide-text">{{ $t('chat.agent.controls-guide.send-voice.description') }}</span>
                  </div>
                </v-card>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Chat Dialog -->
      <v-dialog v-model="chatVisible" max-width="600" scrollable>
        <ChatBotComponent v-if="chatVisible" :initialMessage="transcribedText" @close="chatVisible = false" />
      </v-dialog>
    </teleport>
  </div>
</template>

<script setup>
  import { ref, nextTick, onMounted, onUnmounted } from 'vue'
  import { VoiceRecorder } from 'capacitor-voice-recorder'
  import lottie from 'lottie-web'
  import Swal from 'sweetalert2'
  import ChatBotComponent from '@/ai/aiComponents/ChatBotComponent.vue'
  import lottieAgent from '@/ai/aiAnimations/lottie_customer_service.json'
  import lottieAgentActive from '@/ai/aiAnimations/lottie_customer_active.json'
  import bubbleAnimation from '@/ai/aiAnimations/voice.json'
  import activateSoundUrl from '@/ai/aiSounds/activate.mp3'
  import deactivateSoundUrl from '@/ai/aiSounds/deactivate.mp3'
  import { sendToAssistant } from '@/ai/aiServices/assistantService'
  import { loadMessages, saveMessages } from '@/ai/aiServices/chatStorage'
  import { speakWithElevenLabs, detectLanguage } from '@/ai/aiServices/tts'
  import { elevenLabsSTT, deepgramSTT } from '@/ai/aiServices/speechToText'
  import { speakWithDeepgram } from '../aiServices/tts_deepgram'
  import { showToast } from '@/services/BLUEID_NEWSDK_Helper'
  import { Capacitor } from '@capacitor/core'

  const isDialogOpen = ref(false)
  const chatVisible = ref(false)
  const isAndroid = ref(Capacitor.getPlatform() === 'android')

  const transcribedText = ref('')
  const isPaused = ref(false)
  const isTextSending = ref(false)
  const isVoiceSending = ref(false)
  const isCancelling = ref(false)
  const isPausing = ref(false)
  const hasStartedSpeaking = ref(false)
  const showDelayMessage = ref(false)
  const detectedLanguage = ref('en')

  const lottieContainer = ref(null)
  const lottieAgentRef = ref(null)
  let lottieInstance = null
  let lottieAgentInstance = null

  const activateSound = new Audio(activateSoundUrl)
  const deactivateSound = new Audio(deactivateSoundUrl)
  const isIosNative = Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform()

  onMounted(() => {
      if (lottieAgentRef.value) {
          lottieAgentInstance = lottie.loadAnimation({
              container: lottieAgentRef.value,
              renderer: 'svg',
              loop: true,
              autoplay: true,
              animationData: lottieAgent,
          })
      }
  })

  onUnmounted(() => {
      lottieInstance?.destroy()
      lottieAgentInstance?.destroy()
  })

  function resetAllState() {
      isDialogOpen.value = false
      isTextSending.value = false
      isVoiceSending.value = false
      isPaused.value = false
      isPausing.value = false
      isCancelling.value = false
      hasStartedSpeaking.value = false
      transcribedText.value = ''
  }

  async function handleAvatarClick() {
      await VoiceRecorder.stopRecording().catch(() => { })
      await openDialog()
  }

  function showPermissionDenied() {
      Swal.fire({ icon: 'warning', title: 'Permission Denied', text: 'Microphone access is required.' })
  }

  function showError(title, message) {
      Swal.fire({ icon: 'error', title, text: message })
  }

  function swapLottieAnimation(animationData) {
      if (lottieContainer.value) {
          lottieInstance?.destroy()
          lottieInstance = lottie.loadAnimation({
              container: lottieContainer.value,
              renderer: 'svg',
              loop: true,
              autoplay: true,
              animationData,
          })
      }
  }

  async function ensureSpeechPermission() {
      const canRecord = await VoiceRecorder.canDeviceVoiceRecord()
      if (!canRecord.value) return false
      const hasPermission = await VoiceRecorder.hasAudioRecordingPermission()
      if (!hasPermission.value) {
          const result = await VoiceRecorder.requestAudioRecordingPermission()
          return result.value === true
      }
      return true
  }

  async function openDialog() {
      isDialogOpen.value = true
      hasStartedSpeaking.value = false
      await nextTick()
      preloadLottie()
      lottieInstance?.pause()
      const status = await VoiceRecorder.getCurrentStatus()
      if (status.status !== 'NONE') await VoiceRecorder.stopRecording()
  }

  function preloadLottie() {
      if (lottieContainer.value) {
          lottieInstance?.destroy()
          lottieInstance = lottie.loadAnimation({
              container: lottieContainer.value,
              renderer: 'svg',
              loop: true,
              autoplay: true,
              animationData: bubbleAnimation,
          })
      }
  }

  async function startSpeaking() {
      const granted = await ensureSpeechPermission()
      if (!granted) return showPermissionDenied()

      const status = await VoiceRecorder.getCurrentStatus()
      if (status.status !== 'NONE') await VoiceRecorder.stopRecording()

      lottieInstance?.play()
      await VoiceRecorder.startRecording()
      if (!isIosNative) {
          activateSound.play().catch(() => { })
      }

      hasStartedSpeaking.value = true
      isPaused.value = false
  }

  async function togglePause() {
      isPausing.value = true
      try {
          if (isPaused.value) {
              await VoiceRecorder.resumeRecording()
              lottieInstance?.play()
          } else {
              await VoiceRecorder.pauseRecording()
              lottieInstance?.pause()
          }
          isPaused.value = !isPaused.value
      } catch (err) {
          showError('Mic Error', err.message)
      } finally {
          isPausing.value = false
      }
  }

  async function handleCancel() {
      try {
          const status = await VoiceRecorder.getCurrentStatus()
          if (status.status !== 'NONE') await VoiceRecorder.stopRecording()
          deactivateSound.play().catch(() => { })
      } catch (err) {
          showError('Cancel Error', err.message)
      } finally {
          resetAllState()
      }
  }

  function openChatWindow() {
      VoiceRecorder.stopRecording().catch(() => { })
      deactivateSound.play().catch(() => { })
      isDialogOpen.value = false
      if (chatVisible.value) {
          window.dispatchEvent(new CustomEvent('voice-transcribed', { detail: transcribedText.value }))
      } else {
          chatVisible.value = true
      }
      transcribedText.value = ''
  }

  async function handleVoiceReply() {
      if (!hasStartedSpeaking.value) {
          return showToast('Please start speaking or open chat', 'warning')
      }

      isVoiceSending.value = true

      try {
          const result = await VoiceRecorder.stopRecording()
          const base64 = result?.value?.recordDataBase64
          if (!base64) throw new Error('No audio data')

          const blob = base64ToBlob(base64, result.value.mimeType || 'audio/webm')

          const { text: cleanText, language_code } = await elevenLabsSTT(blob)
          if (!cleanText) return

          swapLottieAnimation(lottieAgent)

          transcribedText.value = cleanText
          console.log('📝 Transcribed:', cleanText)
          console.log('🌐 STT Language Code:', language_code)

          const lang = language_code?.toLowerCase().startsWith('de') ? 'de' : 'en'
          detectedLanguage.value = lang
          const prefix = lang === 'de'
              ? 'Bitte antworte auf Deutsch und optimiere deine Antwort für die Sprachausgabe (locker gesprochen).\n'
              : 'Please reply in English and optimize your response for voice delivery (natural spoken).\n'

          showDelayMessage.value = false
          const delayTimer = setTimeout(() => {
              showDelayMessage.value = true
          }, 3000)

          const assistantResponse = await sendToAssistant(prefix + cleanText)
          const reply = assistantResponse?.reply?.trim()
          if (!reply) return

          let audio
          if (lang === 'en') {
              audio = await speakWithDeepgram(reply)
          } else {
              audio = await speakWithElevenLabs(reply, 'de')
          }

          clearTimeout(delayTimer)
          showDelayMessage.value = false

          if (audio) {
              audio.onended = () => {
                  swapLottieAnimation(bubbleAnimation)
                  lottieInstance?.pause()
              }
          } else {
              swapLottieAnimation(bubbleAnimation)
              lottieInstance?.pause()
          }

          const old = (await loadMessages()) || []
          old.push({ type: 'user', text: cleanText }, { type: 'bot', text: reply })
          await saveMessages(old)

      } catch (err) {
          showError('Voice Reply Error', err.message)
          swapLottieAnimation(bubbleAnimation)
          lottieInstance?.stop()
      } finally {
          deactivateSound.play().catch(() => { })
          isVoiceSending.value = false
          hasStartedSpeaking.value = false
          isPaused.value = false
      }
  }

  async function handleTextReply() {
      if (!hasStartedSpeaking.value) return showToast('Please start speaking or open chat', 'warning')
      isTextSending.value = true
      try {
          const result = await VoiceRecorder.stopRecording()
          const base64 = result.value.recordDataBase64
          if (!base64) throw new Error('No audio data')
          const blob = base64ToBlob(base64, result.value.mimeType || 'audio/webm')

          transcribedText.value = await elevenLabsSTT(blob)
          if (chatVisible.value) {
              window.dispatchEvent(new CustomEvent('voice-transcribed', { detail: transcribedText.value }))
          } else {
              chatVisible.value = true
          }
          transcribedText.value = ''
      } catch (err) {
          showError('Transcription Error', err.message)
      } finally {
          deactivateSound.play().catch(() => { })
          isTextSending.value = false
      }
  }

  function base64ToBlob(base64, mimeType) {
      const byteCharacters = atob(base64)
      const byteArrays = []
      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512)
          const byteNumbers = Array.from(slice).map((c) => c.charCodeAt(0))
          byteArrays.push(new Uint8Array(byteNumbers))
      }
      return new Blob(byteArrays, { type: mimeType })
  }
</script>

<style scoped>
  @import '@/ai/aiStyles/AgentStyle.css';
</style>
